import React, { useState } from "react";
import axios from "axios";
import { Aurora, Card, PageHeader, Logo, Stepper } from "./Shell";
import Instructions from "./Instructions";
import { useDemo, useDemoStep } from "./DemoContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [  {
    key: "self_harm",
    label: "Are you having active thoughts of self-harm?",
    severity: "red",
    invert: false,
  },
  {
    key: "mental_health_crisis",
    label: "Are you currently in a mental health crisis?",
    severity: "red",
    invert: false,
  },
  {
    key: "dissociative_symptoms",
    label: "Do you feel disconnected from your body or surroundings right now?",
    severity: "amber",
    invert: false,
  },
  {
    key: "recent_severe_trauma",
    label: "Have you experienced a deeply distressing event in the past 30 days?",
    severity: "amber",
    invert: false,
  },
  {
    key: "safe_quiet_location",
    label: "Are you in a safe, quiet, undisturbed location?",
    severity: "amber",
    invert: true,
  },
  {
    key: "prior_emdr",
    label: "Have you previously worked with EMDR or bilateral stimulation?",
    severity: "info",
    invert: false,
  },
];

// Static "how this screener works" guidance — extracted so we don't
// allocate a new array on every render.
const SCREENER_INSTRUCTIONS = [
  { strong: "Answer truthfully", text: "your honesty determines whether this tool is the right next step for you right now." },
  { strong: "Red flags", text: "active thoughts of self-harm or a mental-health crisis will pause the session and surface immediate-support resources (988, 741741, 911)." },
  { strong: "Amber flags", text: "things like dissociation, recent severe trauma, or an unsafe location don't block the session — but tell us to be gentler." },
  { strong: "Privacy", text: "your answers are stored only as part of your own wellness record. No third parties." },
];

export default function SafetyScreener({ onPass, onBlocked, stepIndex, steps }) {
  const [answers, setAnswers] = useState({
    self_harm: null,
    mental_health_crisis: null,
    dissociative_symptoms: null,
    recent_severe_trauma: null,
    safe_quiet_location: null,
    prior_emdr: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const allAnswered = Object.values(answers).every((v) => v !== null);

  const set = (k, v) => setAnswers((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/screener`, answers);
      if (data.blocked || data.red_flag) {
        onBlocked(data);
      } else {
        onPass(data);
      }
    } catch (e) {
      setError("Unable to submit screener. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Demo auto-fill: set all NO (safe path), then POST directly with the
  // fresh values (skipping the stale-closure trap on `answers` state).
  const demo = useDemo();
  useDemoStep(() => {
    const demoAnswers = {
      self_harm: false,
      mental_health_crisis: false,
      dissociative_symptoms: false,
      recent_severe_trauma: false,
      safe_quiet_location: true,
      prior_emdr: false,
    };
    setAnswers(demoAnswers);
    setTimeout(async () => {
      if (!demo.active) return;
      setSubmitting(true);
      try {
        const { data } = await axios.post(`${API}/screener`, demoAnswers);
        if (data.blocked || data.red_flag) onBlocked(data);
        else onPass(data);
      } catch (e) {
        setError("Demo submit failed.");
      } finally {
        setSubmitting(false);
      }
    }, 1400);
  }, 1500);

  return (
    <Aurora>
      <div className="relative max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-center justify-between mb-12">
          <Logo size="md" />
          <Stepper steps={steps} current={stepIndex} />
        </div>

        <PageHeader
          eyebrow="Step 01 · Safety Screener"
          title="Before we begin"
          subtitle="Six brief questions. Your answers stay private and help us route you to the right care if needed."
        />

        <Instructions title="How this screener works" items={SCREENER_INSTRUCTIONS} />

        <div className="space-y-4 fade-up">
          {QUESTIONS.map((q, i) => (
            <Card key={q.key} className="!p-6">
              <div className="flex items-start justify-between gap-6 flex-col sm:flex-row">
                <div className="flex-1">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--text-secondary)] mb-1">
                    Q{String(i + 1).padStart(2, "0")} ·{" "}
                    <span
                      style={{
                        color:
                          q.severity === "red"
                            ? "var(--danger)"
                            : q.severity === "amber"
                            ? "var(--warning)"
                            : "var(--accent-cyan)",
                      }}
                    >
                      {q.severity}
                    </span>
                  </div>
                  <div className="text-ink text-lg font-light leading-snug">
                    {q.label}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => set(q.key, true)}
                    className={`px-5 py-2 rounded-full text-sm font-mono tracking-widest transition-all border ${
                      answers[q.key] === true
                        ? "bg-[color:var(--accent-cyan-muted)] border-[color:var(--accent-cyan)] text-[color:var(--accent-cyan)]"
                        : "border-[color:var(--hairline)] text-[color:var(--text-secondary)] hover:border-[color:rgba(15,26,46,0.20)]"
                    }`}
                    data-testid={`screener-${q.key}-yes`}
                  >
                    YES
                  </button>
                  <button
                    type="button"
                    onClick={() => set(q.key, false)}
                    className={`px-5 py-2 rounded-full text-sm font-mono tracking-widest transition-all border ${
                      answers[q.key] === false
                        ? "bg-[color:var(--accent-cyan-muted)] border-[color:var(--accent-cyan)] text-[color:var(--accent-cyan)]"
                        : "border-[color:var(--hairline)] text-[color:var(--text-secondary)] hover:border-[color:rgba(15,26,46,0.20)]"
                    }`}
                    data-testid={`screener-${q.key}-no`}
                  >
                    NO
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {error && (
          <div className="mt-6 text-[color:var(--danger)] text-sm font-mono">
            {error}
          </div>
        )}

        <div className="mt-10 flex items-center gap-4 fade-up">
          <button
            type="button"
            className="btn-primary"
            onClick={submit}
            disabled={!allAnswered || submitting}
            data-testid="screener-submit-btn"
          >
            {submitting ? "Submitting…" : "Continue →"}
          </button>
          {!allAnswered && (
            <div className="text-xs font-mono text-[color:var(--text-secondary)]">
              Answer all six to continue.
            </div>
          )}
        </div>
      </div>
    </Aurora>
  );
}
